/**
 * Service Type Templates for Georgia DBHDD Clinical Documentation
 * 
 * Each template defines:
 *  - billingCode: The HCPCS code for Medicaid billing
 *  - billingLabel: Human-readable billing description
 *  - staffTitle: How to refer to the clinician in the note
 *  - requiredElements: What auditors check for in this service type
 *  - typicalInterventions: Service-specific intervention language
 *  - goalLanguage: How goals should be framed for this service
 *  - promptAdditions: Extra instructions for the AI prompt
 *  - jsonOverrides: Any changes to the JSON structure for this type
 */

const SERVICE_TEMPLATES = {

  // ===== INTENSIVE CASE MANAGEMENT (ICM) =====
  "ICM Weekly Visit": {
    billingCode: "H0039",
    billingLabel: "Assertive Community Treatment / ICM",
    staffTitle: "ICM Staff",
    requiredElements: [
      "Face-to-face contact in community setting",
      "Assessment of current functioning and needs",
      "Coordination of services and resources",
      "Monitoring of treatment plan goals",
      "Safety assessment including SI/HI screening",
      "Medication compliance check",
      "Documentation of individual's response to interventions",
    ],
    typicalInterventions: [
      "Reviewed Safety Plan — Actively listened to Ind as he/she spoke about history and current issues",
      "Asked Ind about goals for ICM program participation",
      "Addressed resource needs — [specific resources discussed]",
      "Addressed skills deficits — [specific skills worked on]",
      "Reviewed medications and medication usage, discussed current supply and plan to obtain refills",
      "Provided cards with ICM Crisis number and scheduled appointment information",
      "Reviewed outpatient appointments needed and assisted Ind to schedule appointments",
      "Discussed supported employment/housing/education as applicable",
      "Staff asked Ind about health and wellness goals",
      "ICM Staff scheduled next ICM meeting with Ind",
    ],
    goalLanguage: "Ind will engage in ICM services to maintain stability in the community, improve daily living skills, and progress toward recovery goals as outlined in the treatment plan.",
    promptAdditions: `
SERVICE-SPECIFIC REQUIREMENTS FOR ICM (H0039):
- Document that this was a face-to-face contact in a community setting
- Include assessment of current functioning across domains: housing, employment, social, medical, psychiatric
- Document coordination with other service providers if applicable
- Show evidence of monitoring treatment plan goals
- Include safety screening (SI/HI/psychosis check)
- Document medication compliance status
- Note any crisis intervention if applicable
- Ensure interventions connect to treatment plan goals
- Document the individual's response to each intervention
- Include plan for next contact with specific date/time if discussed`,
  },

  "ICM Initial Assessment": {
    billingCode: "H0039",
    billingLabel: "Assertive Community Treatment / ICM — Initial",
    staffTitle: "ICM Staff",
    requiredElements: [
      "Comprehensive biopsychosocial assessment",
      "Psychiatric history and current symptoms",
      "Substance use history",
      "Housing and living situation assessment",
      "Support system evaluation",
      "Risk assessment",
      "Initial treatment plan goals discussed",
    ],
    typicalInterventions: [
      "Completed initial assessment with Ind at [location]",
      "Gathered psychiatric history including hospitalizations, diagnoses, and treatment history",
      "Assessed current medication regimen and compliance",
      "Evaluated housing stability and living conditions",
      "Assessed substance use history and current status",
      "Completed risk assessment including SI/HI screening",
      "Discussed ICM program expectations and individual's goals for participation",
      "Reviewed crisis resources and safety planning",
      "Scheduled follow-up contact",
    ],
    goalLanguage: "Complete initial assessment and develop individualized treatment plan goals with Ind to support community-based recovery.",
    promptAdditions: `
SERVICE-SPECIFIC REQUIREMENTS FOR ICM INITIAL ASSESSMENT:
- This is a FIRST CONTACT — document more comprehensively than a routine visit
- Include thorough biopsychosocial information gathered
- Document psychiatric history, substance use history, legal history
- Assess all life domains: housing, employment, education, social, medical, psychiatric
- Document the individual's own stated goals for treatment
- Include a thorough risk assessment
- Note any immediate needs or crises identified
- Document the plan for ongoing ICM services`,
  },

  // ===== PSYCHOSOCIAL REHABILITATION (PSR) =====
  "PSR Session": {
    billingCode: "H2017",
    billingLabel: "Psychosocial Rehabilitation",
    staffTitle: "PSR Specialist",
    requiredElements: [
      "Skill-building activity clearly documented",
      "Connection to specific treatment plan goal",
      "Individual's participation level documented",
      "Progress or barriers to skill acquisition",
      "Community integration component",
      "Recovery-oriented language throughout",
    ],
    typicalInterventions: [
      "Facilitated skill-building activity focused on [specific skill domain]",
      "Assisted Ind with developing [daily living / social / coping / vocational] skills",
      "Modeled appropriate [behavior/skill] and provided opportunity for Ind to practice",
      "Provided psychoeducation on [topic relevant to treatment goal]",
      "Accompanied Ind to [community resource] to practice community integration skills",
      "Reviewed and practiced coping strategies for managing [symptoms/triggers]",
      "Assisted Ind with [budgeting/cooking/hygiene/transportation/social skills]",
      "Encouraged Ind's engagement in recovery-oriented activities",
      "Processed Ind's experience with skill practice and identified areas for continued development",
    ],
    goalLanguage: "Ind will develop and demonstrate [specific skill] to increase independence and support community integration as outlined in the treatment plan.",
    promptAdditions: `
SERVICE-SPECIFIC REQUIREMENTS FOR PSR (H2017):
- PSR is SKILL-BUILDING — every intervention must document a specific skill being taught, practiced, or reinforced
- This is NOT case management. Do NOT document resource coordination or appointment scheduling as primary interventions
- Document what SKILL was targeted, HOW it was practiced, and the Ind's RESPONSE/PROGRESS
- Valid PSR skill domains: daily living skills, social skills, coping skills, vocational skills, community integration, symptom management, medication self-management, budgeting/financial literacy, personal hygiene/grooming, cooking/nutrition, transportation navigation, interpersonal communication
- Use recovery-oriented language: "developing skills," "building capacity," "increasing independence"
- Document the individual's level of participation and engagement
- Note specific progress: "Ind demonstrated ability to..." or "Ind required verbal prompting to..."
- Include community integration: where the skill was practiced, real-world application
- The RESPONSE section should detail HOW Ind performed the skill, not just that they participated
- Staff role: "PSR Specialist" not "ICM Staff"`,
  },

  // ===== COMMUNITY SUPPORT TEAM (CST) =====
  "CST Visit": {
    billingCode: "H2015",
    billingLabel: "Community Support — Individual",
    staffTitle: "CST Staff",
    requiredElements: [
      "Community-based support service documented",
      "Connection to treatment plan goal",
      "Individual's current level of functioning",
      "Support provided in natural environment",
      "Crisis prevention or intervention if applicable",
      "Coordination with treatment team",
    ],
    typicalInterventions: [
      "Provided community support services to Ind at [location]",
      "Assessed Ind's current functioning and identified support needs",
      "Assisted Ind with [specific community support activity]",
      "Coordinated with [treatment team member/provider] regarding Ind's care",
      "Provided supportive counseling to address [current stressor]",
      "Assisted Ind with navigating community resources for [specific need]",
      "Monitored Ind's medication compliance and side effects",
      "Reviewed safety plan and crisis resources",
      "Encouraged Ind's continued engagement in treatment services",
    ],
    goalLanguage: "Ind will engage in community support services to maintain stability, access needed resources, and develop skills for independent community living.",
    promptAdditions: `
SERVICE-SPECIFIC REQUIREMENTS FOR CST (H2015):
- Document that services were provided in the individual's natural community environment
- Show support activities that maintain community tenure
- Include assessment of current functioning level
- Document any coordination with the broader treatment team
- Note prevention-focused interventions (preventing hospitalization, homelessness, crisis)
- Staff role: "CST Staff" not "ICM Staff"
- Ensure documentation supports the community support billing code
- Include evidence of person-centered, recovery-oriented approach`,
  },

  // ===== PEER SUPPORT =====
  "Peer Support": {
    billingCode: "H0038",
    billingLabel: "Peer Support Services",
    staffTitle: "Peer Support Specialist",
    requiredElements: [
      "Peer-to-peer relationship clearly documented",
      "Shared lived experience referenced appropriately",
      "Recovery-oriented activities",
      "Self-advocacy skill development",
      "Community resource navigation",
      "Wellness and recovery plan engagement",
    ],
    typicalInterventions: [
      "Engaged with Ind in peer-to-peer support at [location]",
      "Shared recovery experience to provide hope and encouragement",
      "Assisted Ind in identifying personal strengths and recovery goals",
      "Supported Ind in developing self-advocacy skills",
      "Accompanied Ind to [community resource/recovery meeting/appointment]",
      "Discussed wellness strategies and coping skills from a peer perspective",
      "Encouraged Ind's participation in recovery-oriented community activities",
      "Assisted Ind with navigating the behavioral health system",
      "Reviewed Wellness Recovery Action Plan (WRAP) components with Ind",
      "Modeled recovery-oriented behaviors and positive coping",
    ],
    goalLanguage: "Ind will engage in peer support services to develop self-advocacy skills, build a recovery support network, and maintain progress toward personal recovery goals.",
    promptAdditions: `
SERVICE-SPECIFIC REQUIREMENTS FOR PEER SUPPORT (H0038):
- Peer Support is UNIQUE — the relationship is peer-to-peer, NOT clinical
- Staff role: "Peer Support Specialist" (CPS) — NOT "ICM Staff" or "clinician"
- Document the PEER relationship: shared experience, mutual understanding, hope
- Do NOT use clinical assessment language (no "assessed," "evaluated," "diagnosed")
- Instead use: "discussed," "shared," "supported," "encouraged," "accompanied"
- Focus on recovery, wellness, self-determination, empowerment
- Document activities that build the Ind's own recovery capital
- Include community connection: recovery meetings, support groups, community resources
- Reference the Wellness Recovery Action Plan (WRAP) if discussed
- Peer support is about MODELING recovery, not providing treatment
- The tone should be warmer and more personal than clinical notes
- Document Ind's own voice: what THEY want, what THEY are working toward`,
  },

  // ===== CRISIS STABILIZATION =====
  "Crisis Intervention": {
    billingCode: "H2011",
    billingLabel: "Crisis Intervention Services",
    staffTitle: "Crisis Staff",
    requiredElements: [
      "Nature of the crisis clearly documented",
      "Risk assessment completed (SI/HI/psychosis)",
      "De-escalation interventions used",
      "Safety plan created or updated",
      "Disposition and follow-up plan",
      "Notification to treatment team if applicable",
    ],
    typicalInterventions: [
      "Responded to crisis contact for Ind at [location/by phone]",
      "Completed risk assessment: assessed for suicidal ideation, homicidal ideation, psychotic symptoms",
      "Utilized de-escalation techniques including [active listening/validation/grounding]",
      "Assisted Ind in identifying triggers and warning signs",
      "Created/updated safety plan with Ind including coping strategies and emergency contacts",
      "Assessed for need for higher level of care (ED evaluation, CSU, inpatient)",
      "Coordinated with [treatment team/family/emergency services] as indicated",
      "Provided crisis resources including 988 Suicide & Crisis Lifeline and local crisis line",
      "Monitored Ind until crisis stabilized and Ind verbalized ability to remain safe",
      "Scheduled follow-up contact within 24-48 hours",
    ],
    goalLanguage: "Stabilize current crisis, ensure individual safety, and connect to appropriate level of care and ongoing support services.",
    promptAdditions: `
SERVICE-SPECIFIC REQUIREMENTS FOR CRISIS INTERVENTION (H2011):
- CRISIS documentation has the HIGHEST scrutiny — be thorough and precise
- Document the NATURE of the crisis: what happened, what triggered it, how Ind presented
- Include a COMPLETE risk assessment: SI (with plan/intent/means), HI, psychosis, substance use
- Document SPECIFIC de-escalation techniques used and Ind's response to each
- Safety plan MUST be documented: warning signs, coping strategies, support contacts, crisis numbers
- Document the DISPOSITION: Was Ind stabilized? Referred to ED? Admitted? Sent home with safety plan?
- Include any MANDATORY REPORTING actions taken
- Document any COORDINATION: who was notified (family, treatment team, emergency services)
- Include a FOLLOW-UP PLAN: when next contact will occur (should be within 24-48 hours)
- If medications were discussed or administered, document specifically
- Staff role: "Crisis Staff" not "ICM Staff"
- This note may be subpoenaed — document as if a judge will read it`,
  },

  // ===== ASSERTIVE COMMUNITY TREATMENT (ACT) =====
  "ACT Contact": {
    billingCode: "H0039",
    billingLabel: "Assertive Community Treatment",
    staffTitle: "ACT Team Member",
    requiredElements: [
      "Multidisciplinary team approach documented",
      "Community-based contact",
      "Treatment plan goal monitoring",
      "Coordination with other ACT team members",
      "Crisis prevention and intervention",
      "Functional assessment of daily living",
    ],
    typicalInterventions: [
      "Conducted ACT team visit with Ind at [location]",
      "Assessed current psychiatric status and medication compliance",
      "Monitored symptoms and functional status across life domains",
      "Coordinated with ACT team regarding Ind's current treatment needs",
      "Provided [specific intervention: medication education, skills training, supportive counseling, resource linkage]",
      "Assessed housing stability and daily living functioning",
      "Reviewed and updated treatment plan goals based on current progress",
      "Provided crisis prevention interventions as indicated",
      "Scheduled next ACT team contact",
    ],
    goalLanguage: "Ind will engage with ACT team services to maintain psychiatric stability, develop community living skills, and prevent hospitalization.",
    promptAdditions: `
SERVICE-SPECIFIC REQUIREMENTS FOR ACT (H0039):
- ACT is a TEAM-based approach — reference the multidisciplinary team when relevant
- Document which ACT team role is providing this contact (nurse, case manager, peer, psychiatrist)
- Show community-based, in-vivo service delivery
- ACT serves individuals with the most serious mental illness — documentation should reflect high acuity
- Include assessment across ALL functional domains: psychiatric, medical, housing, employment, social, legal, substance use
- Document any team coordination or staffing discussions about this individual
- Show intensive service delivery (ACT teams provide multiple contacts per week)
- Include crisis prevention focus: what are the early warning signs, what is being done proactively
- Staff role: "ACT Team Member" not just "ICM Staff"`,
  },
};

// ===== HELPER FUNCTIONS =====

/**
 * Get template for a service type. Falls back to ICM if not found.
 */
export function getServiceTemplate(serviceType) {
  // Try exact match first
  if (SERVICE_TEMPLATES[serviceType]) {
    return SERVICE_TEMPLATES[serviceType];
  }

  // Try fuzzy match
  const key = Object.keys(SERVICE_TEMPLATES).find(k =>
    k.toLowerCase().includes(serviceType.toLowerCase()) ||
    serviceType.toLowerCase().includes(k.toLowerCase())
  );

  if (key) return SERVICE_TEMPLATES[key];

  // Map common variations
  const aliases = {
    "icm": "ICM Weekly Visit",
    "icm visit": "ICM Weekly Visit",
    "icm weekly": "ICM Weekly Visit",
    "weekly visit": "ICM Weekly Visit",
    "initial assessment": "ICM Initial Assessment",
    "initial": "ICM Initial Assessment",
    "intake": "ICM Initial Assessment",
    "psr": "PSR Session",
    "psychosocial": "PSR Session",
    "psychosocial rehab": "PSR Session",
    "skills training": "PSR Session",
    "skill building": "PSR Session",
    "cst": "CST Visit",
    "community support": "CST Visit",
    "peer": "Peer Support",
    "peer support": "Peer Support",
    "cps": "Peer Support",
    "crisis": "Crisis Intervention",
    "crisis intervention": "Crisis Intervention",
    "crisis stabilization": "Crisis Intervention",
    "act": "ACT Contact",
    "act team": "ACT Contact",
    "assertive community": "ACT Contact",
  };

  const normalized = serviceType.toLowerCase().trim();
  if (aliases[normalized] && SERVICE_TEMPLATES[aliases[normalized]]) {
    return SERVICE_TEMPLATES[aliases[normalized]];
  }

  // Default to ICM
  return SERVICE_TEMPLATES["ICM Weekly Visit"];
}

/**
 * Get all available service types for dropdowns
 */
export function getServiceTypeOptions() {
  return Object.keys(SERVICE_TEMPLATES).map(key => ({
    value: key,
    label: key,
    billingCode: SERVICE_TEMPLATES[key].billingCode,
  }));
}

/**
 * Get billing code for a service type
 */
export function getBillingCode(serviceType) {
  const template = getServiceTemplate(serviceType);
  return `${template.billingCode} — ${template.billingLabel}`;
}

export default SERVICE_TEMPLATES;